import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import { Package, User, Phone, FileText, Clock, Edit, Trash2, ArrowRight, Save, X, CheckCircle, AlertCircle, Truck, CreditCard, Box } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAppContext } from '../../hooks/useAppContext';

// Types aligned with list screen
type PackageStatus = 'NEW' | 'PAYMENT_CONFIRMED' | 'PROCESSING' | 'READY_FOR_DELIVERY' | 'DELIVERED' | 'CANCELLED';
interface PackageRequest {
    id: string;
    title: string;
    description: string;
    customerName: string;
    customerPhone: string;
    priority: 'low' | 'medium' | 'high';
    status: PackageStatus;
    progressPercent: number;
    creationDate: string;
    lastModified: string;
    employeeId: string;
    employeeName?: string;
    branch: string;
    customerLocation?: string;
    meta?: Record<string, any>;
}

interface AttachmentItem {
    url: string;
    fileName?: string;
    type?: string;
}

interface PackageDetailsResponse extends PackageRequest {
    attachments?: {
        paymentProofs?: AttachmentItem[];
        shippingDocs?: AttachmentItem[];
        all?: AttachmentItem[];
    };
    logs?: Array<{ id: string; action: string; comment?: string; actorId?: string; date?: string }>;
}

const statusBadge = (status: PackageStatus) => {
    switch (status) {
        case 'NEW': return <span className="px-3 py-1 rounded-lg bg-blue-500/20 text-blue-400 border border-blue-500/30 text-xs font-medium">جديد</span>;
        case 'PAYMENT_CONFIRMED': return <span className="px-3 py-1 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-medium">تم تأكيد الدفع</span>;
        case 'PROCESSING': return <span className="px-3 py-1 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 text-xs font-medium">قيد المعالجة</span>;
        case 'READY_FOR_DELIVERY': return <span className="px-3 py-1 rounded-lg bg-purple-500/20 text-purple-400 border border-purple-500/30 text-xs font-medium">جاهز للتسليم</span>;
        case 'DELIVERED': return <span className="px-3 py-1 rounded-lg bg-green-500/20 text-green-400 border border-green-500/30 text-xs font-medium">تم التسليم</span>;
        case 'CANCELLED': return <span className="px-3 py-1 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30 text-xs font-medium">ملغي</span>;
        default: return <span className="px-3 py-1 rounded-lg bg-slate-500/20 text-slate-400 border border-slate-500/30 text-xs font-medium">غير معروف</span>;
    }
};

const ModernPackageDetailScreen: React.FC = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const backPath = location.pathname.startsWith('/admin-packages') ? '/admin-packages' : '/packages';
    const initial: PackageRequest | undefined = (location.state as any)?.initial;
    const { user } = useAppContext();

    const [details, setDetails] = useState<PackageDetailsResponse | null>(initial || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({
        title: '', description: '', customerName: '', customerPhone: '', priority: 'low' as 'low' | 'medium' | 'high', status: 'NEW' as PackageStatus, progressPercent: 0
    });

    useEffect(() => {
        let ignore = false;
        const fetchDetails = async () => {
            if (!id) return;
            try {
                setLoading(true);
                setError(null);
                const res = await fetch(`${API_BASE_URL}/package-requests/${id}`);
                if (!res.ok) {
                    let msg = 'تعذر تحميل تفاصيل الطلب';
                    try {
                        const ct = res.headers.get('content-type') || '';
                        if (ct.includes('application/json')) {
                            const j = await res.json();
                            if (j && typeof j === 'object' && j.message) {
                                msg = `حدث خطأ: ${j.message}`;
                            }
                        } else {
                            const t = await res.text();
                            if (t && !t.trim().startsWith('{')) {
                                msg = t.trim();
                            }
                        }
                    } catch { }
                    throw new Error(msg);
                }
                const data: PackageDetailsResponse = await res.json();
                if (!ignore) {
                    setDetails(data);
                    setEditForm({
                        title: data.title,
                        description: data.description,
                        customerName: data.customerName,
                        customerPhone: data.customerPhone,
                        priority: data.priority,
                        status: data.status,
                        progressPercent: data.progressPercent
                    });
                }
            } catch (e: any) {
                console.error('Failed to load package details', e);
                if (!ignore) setError(e.message || 'حدث خطأ أثناء تحميل التفاصيل');
            } finally {
                if (!ignore) setLoading(false);
            }
        };
        fetchDetails();
        return () => { ignore = true; };
    }, [id]);

    const formatTimeAgo = (dateStr?: string) => {
        if (!dateStr) return 'وقت غير محدد';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        const diff = Date.now() - d.getTime();
        const sec = Math.floor(diff / 1000);
        const min = Math.floor(sec / 60);
        const hr = Math.floor(min / 60);
        const day = Math.floor(hr / 24);
        const month = Math.floor(day / 30);
        const year = Math.floor(day / 365);
        if (sec < 60) return 'منذ ثوانٍ';
        if (min < 2) return 'منذ دقيقة';
        if (min < 60) return `منذ ${min} دقائق`;
        if (hr < 2) return 'منذ ساعة';
        if (hr < 24) return `منذ ${hr} ساعات`;
        if (day < 2) return 'منذ يوم';
        if (day < 7) return `منذ ${day} أيام`;
        if (Math.floor(day / 7) < 2) return 'منذ أسبوع';
        if (Math.floor(day / 7) < 5) return `منذ ${Math.floor(day / 7)} أسابيع`;
        if (month < 2) return 'منذ شهر';
        if (month < 12) return `منذ ${month} أشهر`;
        if (year < 2) return 'منذ سنة';
        return `منذ ${year} سنوات`;
    };

    const paid = useMemo(() => {
        const s = details?.status;
        return s ? ['PAYMENT_CONFIRMED', 'PROCESSING', 'READY_FOR_DELIVERY', 'DELIVERED'].includes(s) : false;
    }, [details?.status]);

    const stages = ['طلب جديد', 'تأكيد الدفع', 'قيد التنفيذ', 'جاهز للتسليم', 'تم التسليم'];
    const currentStageId = useMemo(() => {
        switch (details?.status) {
            case 'NEW': return 1;
            case 'PAYMENT_CONFIRMED': return 2;
            case 'PROCESSING': return 3;
            case 'READY_FOR_DELIVERY': return 4;
            case 'DELIVERED': return 5;
            case 'CANCELLED': return 1;
            default: return 1;
        }
    }, [details?.status]);

    const handleEdit = () => { setIsEditing(v => !v); };

    const handleDelete = async () => {
        if (!confirm('هل أنت متأكد من حذف الطلب؟ لا يمكن التراجع.')) return;
        try {
            if (!details?.id || !user) { toast.error('الرجاء تسجيل الدخول'); return; }
            const res = await fetch(`${API_BASE_URL}/package-requests/${details.id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ employeeId: user.employeeId })
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ message: 'فشل حذف الطلب' }));
                throw new Error(err.message || 'فشل حذف الطلب');
            }
            toast.success('تم حذف الطلب');
            navigate(backPath);
        } catch (e: any) {
            toast.error(e.message || 'حدث خطأ أثناء حذف الطلب');
        }
    };

    const saveEdit = async () => {
        try {
            if (!details?.id || !user) { toast.error('الرجاء تسجيل الدخول'); return; }
            const res = await fetch(`${API_BASE_URL}/package-requests/${details.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ employeeId: user.employeeId, ...editForm })
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ message: 'فشل تعديل الطلب' }));
                throw new Error(err.message || 'فشل تعديل الطلب');
            }
            const updated = await res.json();
            setDetails(updated);
            toast.success('تم تعديل الطلب');
            setIsEditing(false);
        } catch (e: any) {
            toast.error(e.message || 'حدث خطأ أثناء تعديل الطلب');
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text mb-2">{details?.title || 'تفاصيل الطلب'}</h1>
                    <div className="flex items-center gap-3 text-slate-400">
                        <span className="flex items-center gap-1"><Package size={16} /> {details?.id}</span>
                        <span>•</span>
                        <span className="flex items-center gap-1"><Clock size={16} /> {formatTimeAgo(details?.creationDate)}</span>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {details?.status && statusBadge(details.status)}
                    <button
                        onClick={() => navigate(backPath)}
                        className="px-4 py-2 rounded-xl glass-card hover:bg-white/10 text-slate-300 transition-all flex items-center gap-2"
                    >
                        <ArrowRight size={18} />
                        <span>رجوع</span>
                    </button>
                </div>
            </div>

            {loading && (
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
                </div>
            )}

            {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl flex items-center gap-3">
                    <AlertCircle size={20} />
                    <p>{error}</p>
                </div>
            )}

            {details && !loading && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Status Stepper */}
                        <div className="glass-card p-6 rounded-2xl">
                            <div className="flex justify-between items-center mb-8 relative">
                                <div className="absolute top-1/2 left-0 w-full h-1 bg-slate-800 -z-10 rounded-full"></div>
                                <div className="absolute top-1/2 left-0 h-1 bg-gradient-to-r from-blue-600 to-purple-600 -z-10 rounded-full transition-all duration-500" style={{ width: `${((currentStageId - 1) / (stages.length - 1)) * 100}%` }}></div>

                                {stages.map((stage, idx) => {
                                    const isCompleted = idx + 1 < currentStageId;
                                    const isCurrent = idx + 1 === currentStageId;

                                    return (
                                        <div key={idx} className="flex flex-col items-center gap-2">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all ${isCompleted ? 'bg-emerald-500 border-emerald-500 text-white' :
                                                    isCurrent ? 'bg-slate-900 border-blue-500 text-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.5)]' :
                                                        'bg-slate-900 border-slate-700 text-slate-500'
                                                }`}>
                                                {isCompleted ? <CheckCircle size={16} /> : <span>{idx + 1}</span>}
                                            </div>
                                            <span className={`text-xs font-medium ${isCurrent ? 'text-white' : 'text-slate-500'}`}>{stage}</span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="flex items-center justify-between p-4 bg-slate-800/30 rounded-xl border border-white/5">
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg ${paid ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                        <CreditCard size={20} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-white">حالة الدفع</p>
                                        <p className={`text-xs ${paid ? 'text-emerald-400' : 'text-red-400'}`}>{paid ? 'تم الدفع' : 'غير مدفوع'}</p>
                                    </div>
                                </div>
                                <div className="h-8 w-px bg-white/10"></div>
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-blue-500/20 text-blue-400">
                                        <Box size={20} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-white">نسبة الإنجاز</p>
                                        <p className="text-xs text-blue-400">{details.progressPercent}%</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Details & Edit Form */}
                        <div className="glass-card p-6 rounded-2xl">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                    <FileText className="text-purple-400" size={24} />
                                    تفاصيل الطلب
                                </h2>
                                {!isEditing && (
                                    <button
                                        onClick={handleEdit}
                                        className="p-2 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 text-blue-400 transition-colors"
                                    >
                                        <Edit size={18} />
                                    </button>
                                )}
                            </div>

                            {!isEditing ? (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-xs font-medium text-slate-500 mb-1">اسم العميل</label>
                                            <p className="text-white font-medium flex items-center gap-2">
                                                <User size={16} className="text-slate-400" />
                                                {details.customerName}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-500 mb-1">رقم الهاتف</label>
                                            <p className="text-white font-medium flex items-center gap-2">
                                                <Phone size={16} className="text-slate-400" />
                                                {details.customerPhone}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-500 mb-1">موقع العميل</label>
                                            <p className="text-white font-medium flex items-center gap-2">
                                                <Truck size={16} className="text-slate-400" />
                                                {details.customerLocation || details.meta?.customerLocation || 'غير متوفر'}
                                            </p>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-500 mb-1">الموظف المسؤول</label>
                                            <p className="text-white font-medium flex items-center gap-2">
                                                <User size={16} className="text-slate-400" />
                                                {details.employeeName || details.employeeId}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="pt-6 border-t border-white/5">
                                        <label className="block text-xs font-medium text-slate-500 mb-2">الوصف</label>
                                        <div className="bg-slate-800/30 p-4 rounded-xl text-slate-300 text-sm leading-relaxed whitespace-pre-wrap">
                                            {details.description || 'لا توجد ملاحظات'}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-slate-400 mb-1">العنوان</label>
                                            <input
                                                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-2.5 px-3 text-white focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                value={editForm.title}
                                                onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-400 mb-1">اسم العميل</label>
                                            <input
                                                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-2.5 px-3 text-white focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                value={editForm.customerName}
                                                onChange={e => setEditForm(f => ({ ...f, customerName: e.target.value }))}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-400 mb-1">هاتف العميل</label>
                                            <input
                                                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-2.5 px-3 text-white focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                value={editForm.customerPhone}
                                                onChange={e => setEditForm(f => ({ ...f, customerPhone: e.target.value }))}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-400 mb-1">الأولوية</label>
                                            <select
                                                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-2.5 px-3 text-white focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                value={editForm.priority}
                                                onChange={e => setEditForm(f => ({ ...f, priority: e.target.value as any }))}
                                            >
                                                <option value="low">منخفضة</option>
                                                <option value="medium">متوسطة</option>
                                                <option value="high">مرتفعة</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-400 mb-1">الحالة</label>
                                            <select
                                                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-2.5 px-3 text-white focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                value={editForm.status}
                                                onChange={e => setEditForm(f => ({ ...f, status: e.target.value as PackageStatus }))}
                                            >
                                                <option value="NEW">جديد</option>
                                                <option value="PAYMENT_CONFIRMED">تم تأكيد الدفع</option>
                                                <option value="PROCESSING">قيد المعالجة</option>
                                                <option value="READY_FOR_DELIVERY">جاهز للتسليم</option>
                                                <option value="DELIVERED">تم التسليم</option>
                                                <option value="CANCELLED">ملغي</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-slate-400 mb-1">نسبة التقدم (%)</label>
                                            <input
                                                type="number"
                                                min={0}
                                                max={100}
                                                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-2.5 px-3 text-white focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm"
                                                value={editForm.progressPercent}
                                                onChange={e => setEditForm(f => ({ ...f, progressPercent: Number(e.target.value) }))}
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-medium text-slate-400 mb-1">الوصف</label>
                                            <textarea
                                                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-2.5 px-3 text-white focus:outline-none focus:ring-2 focus:ring-primary/50 text-sm min-h-[100px]"
                                                value={editForm.description}
                                                onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                                        <button
                                            onClick={saveEdit}
                                            className="px-6 py-2.5 rounded-xl bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors flex items-center gap-2"
                                        >
                                            <Save size={18} />
                                            <span>حفظ التعديلات</span>
                                        </button>
                                        <button
                                            onClick={() => setIsEditing(false)}
                                            className="px-6 py-2.5 rounded-xl bg-slate-800 text-slate-300 hover:bg-slate-700 transition-colors flex items-center gap-2"
                                        >
                                            <X size={18} />
                                            <span>إلغاء</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        <div className="glass-card p-6 rounded-2xl">
                            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                <FileText size={20} className="text-amber-400" />
                                المرفقات
                            </h3>

                            <div className="space-y-6">
                                <div>
                                    <h4 className="text-sm font-medium text-slate-400 mb-3">مستندات الدفع</h4>
                                    {details.attachments?.paymentProofs && details.attachments.paymentProofs.length > 0 ? (
                                        <ul className="space-y-2">
                                            {details.attachments.paymentProofs.map((a, idx) => (
                                                <li key={idx} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg border border-white/5">
                                                    <span className="truncate text-xs text-slate-300 max-w-[150px]">{a.fileName || a.url}</span>
                                                    <a className="text-blue-400 text-xs hover:underline" href={a.url} target="_blank" rel="noreferrer">عرض</a>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-xs text-slate-500 italic">لا توجد مستندات دفع</p>
                                    )}
                                </div>

                                <div>
                                    <h4 className="text-sm font-medium text-slate-400 mb-3">مستندات الشحن</h4>
                                    {details.attachments?.shippingDocs && details.attachments.shippingDocs.length > 0 ? (
                                        <ul className="space-y-2">
                                            {details.attachments.shippingDocs.map((a, idx) => (
                                                <li key={idx} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg border border-white/5">
                                                    <span className="truncate text-xs text-slate-300 max-w-[150px]">{a.fileName || a.url}</span>
                                                    <a className="text-blue-400 text-xs hover:underline" href={a.url} target="_blank" rel="noreferrer">عرض</a>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-xs text-slate-500 italic">لا توجد مستندات شحن</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleDelete}
                            className="w-full p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-all flex items-center justify-center gap-2"
                        >
                            <Trash2 size={18} />
                            <span>حذف الطلب</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModernPackageDetailScreen;
